"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  CRITERIA,
  type CraapAnswers,
  type Criterion,
  emptyAnswers,
  RATINGS,
  type Rating,
  scoreCraap,
} from "@/lib/tool-kit/research-workflow/craap";
import { listFormat } from "@/lib/tool-kit/research-workflow/dates";

/** English strings. A translated content file overrides these through `ui`. */
export const CRAAP_TEST_EVALUATOR_STRINGS = {
  worksheetTitle: "CRAAP Test Worksheet",
  sourceHeading: "The source",
  titleLabel: "Title of the source",
  titlePlaceholder: "e.g. Blue Light and Sleep: What the Research Says",
  authorLabel: "Author, publisher or sponsor",
  authorPlaceholder: "e.g. a health website's editorial team",
  urlLabel: "URL or citation",
  urlPlaceholder: "https://…",
  topicLabel: "Your topic or research question",
  topicPlaceholder: "e.g. Does screen use before bed affect students' sleep?",
  dateLabel: "Date evaluated",
  criteria: {
    currency: "Currency",
    relevance: "Relevance",
    authority: "Authority",
    accuracy: "Accuracy",
    purpose: "Purpose",
  },
  definitions: {
    currency: "The timeliness of the information.",
    relevance: "How well it fits your needs.",
    authority: "Who is behind it, and whether they know the subject.",
    accuracy: "How reliable, supported and correct it is.",
    purpose: "Why it exists.",
  },
  questions: {
    currency: [
      "Is it clear when it was published or last updated?",
      "Is it recent enough for your topic? Some fields move fast; others do not.",
      "Has it been revised or updated where the facts have changed?",
      "Do its links and references still work?",
    ],
    relevance: [
      "Does it relate directly to your topic or help answer your question?",
      "Is it written at the right level for you — not too basic, not too advanced?",
      "Did you compare it with other sources before choosing it?",
      "Would you be comfortable citing it in your paper?",
    ],
    authority: [
      "Can you tell who the author, publisher or sponsor is?",
      "Are the author's credentials or affiliations stated?",
      "Is the author qualified to write on this topic?",
      "Is there contact information for the author or publisher?",
      "Does the publisher or web address support its credibility — a journal, a university press, a government agency?",
    ],
    accuracy: [
      "Does it say where its information comes from?",
      "Is it supported by evidence — data, references or citations?",
      "Was it reviewed, refereed or edited before publication?",
      "Can you confirm its main claims in another independent source?",
      "Is the language measured, without emotional appeals?",
      "Is it free of spelling, grammar and typographical errors?",
    ],
    purpose: [
      "Is its main purpose to inform or teach, rather than to sell, entertain or persuade?",
      "Do the authors or sponsors make their purpose clear?",
      "Does it keep fact separate from opinion?",
      "Does the point of view seem objective and fair to other views?",
      "Is it free of undisclosed political, commercial, ideological or personal bias?",
    ],
  },
  ratings: { yes: "Yes", partly: "Partly", no: "No", na: "N/A" },
  notesLabel: "Notes on {criterion}",
  notesPlaceholder:
    "What you checked, and where — the evidence for your answers.",
  resultsHeading: "Scores",
  criterionScore: "{criterion}, out of 10",
  totalLabel: "Total, out of 50",
  noScore: "–",
  progress:
    "one:{count} of {total} question answered|other:{count} of {total} questions answered",
  incomplete:
    "Answer at least one question for each criterion to see the total and a verdict.",
  bandNames: {
    strong: "Strong",
    usable: "Usable with care",
    weak: "Weak",
    unreliable: "Unreliable",
  },
  bandAdvice: {
    strong: "Use it — and still check what other sources say about it.",
    usable: "Usable, but note its weaknesses and corroborate key claims.",
    weak: "Use it only with strong corroboration, or find a better source.",
    unreliable: "Look for a better source.",
  },
  weakSpot:
    "Weak spot: {criteria} scored under 5 out of 10, so the verdict stops at Weak — a high total cannot make up for it.",
  bandKey:
    "Bands: 40–50 strong · 30–39 usable with care · 20–29 weak · under 20 unreliable. The scores and bands are this tool's convention — the CRAAP Test itself is a list of questions.",
  lateral:
    "CRAAP judges a source on its own terms. Fact-checkers also read laterally: they leave the page and see what other sources say about its author and claims. The SIFT moves — Stop, Investigate the source, Find better coverage, Trace claims to their origin — take minutes and catch what a checklist misses.",
  credit:
    "The CRAAP Test was created by Sarah Blakeslee at Meriam Library, California State University, Chico, in 2004.",
  summaryTitle: "CRAAP Test: {title}",
  summaryScore: "{criterion}: {score}/10",
  summaryTotal: "Total: {total}/50 — {band}",
  untitled: "Untitled source",
  copySummary: "Copy summary",
  copied: "Copied!",
  print: "Print worksheet",
  reset: "Start again",
} as const;

type Strings = typeof CRAAP_TEST_EVALUATOR_STRINGS;

/** Questions per criterion — read from the strings, so a translation may add or drop one. */
function countsOf(questions: Strings["questions"]): Record<Criterion, number> {
  return {
    currency: questions.currency.length,
    relevance: questions.relevance.length,
    authority: questions.authority.length,
    accuracy: questions.accuracy.length,
    purpose: questions.purpose.length,
  };
}

const EMPTY_NOTES: Record<Criterion, string> = {
  currency: "",
  relevance: "",
  authority: "",
  accuracy: "",
  purpose: "",
};

function QuestionRow({
  id,
  question,
  value,
  onChange,
  strings,
}: {
  id: string;
  question: string;
  value: Rating | null;
  onChange: (rating: Rating) => void;
  strings: Strings;
}) {
  return (
    <fieldset className="flex flex-col gap-2 border-border border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <legend className="float-left text-foreground text-sm sm:max-w-[60%]">
        {question}
      </legend>
      <div className="flex flex-wrap gap-2">
        {RATINGS.map((rating) => (
          <label
            className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-foreground text-xs has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            key={rating}
          >
            <input
              checked={value === rating}
              className="accent-primary"
              name={id}
              onChange={() => onChange(rating)}
              type="radio"
              value={rating}
            />
            {strings.ratings[rating]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function CraapTestEvaluatorWidget() {
  const strings = useToolUi(CRAAP_TEST_EVALUATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const counts = useMemo(() => countsOf(strings.questions), [strings]);
  const [answers, setAnswers] = useState<CraapAnswers>(() =>
    emptyAnswers(counts)
  );
  const [notes, setNotes] = useState(EMPTY_NOTES);
  const [source, setSource] = useState({
    title: "",
    author: "",
    url: "",
    topic: "",
    date: "",
  });

  const result = useMemo(() => scoreCraap(answers), [answers]);
  const totalQuestions = CRITERIA.reduce((n, c) => n + counts[c], 0);
  const answered = result.criteria.reduce((n, c) => n + c.answered, 0);
  const score = (value: number | null) =>
    value === null ? strings.noScore : fmt(value, { maximumFractionDigits: 1 });

  const setAnswer = (criterion: Criterion, index: number, rating: Rating) =>
    setAnswers((current) => ({
      ...current,
      [criterion]: current[criterion].map((old, i) =>
        i === index ? rating : old
      ),
    }));

  const reset = () => {
    setAnswers(emptyAnswers(counts));
    setNotes(EMPTY_NOTES);
    setSource({ title: "", author: "", url: "", topic: "", date: "" });
  };

  const summary = [
    t(strings.summaryTitle, { title: source.title.trim() || strings.untitled }),
    ...result.criteria.map((c) =>
      t(strings.summaryScore, {
        criterion: strings.criteria[c.criterion],
        score: score(c.score),
      })
    ),
    result.total !== null && result.band
      ? t(strings.summaryTotal, {
          total: score(result.total),
          band: strings.bandNames[result.band],
        })
      : strings.incomplete,
  ].join("\n");

  const sourceFields = [
    ["title", strings.titleLabel, strings.titlePlaceholder],
    ["author", strings.authorLabel, strings.authorPlaceholder],
    ["url", strings.urlLabel, strings.urlPlaceholder],
    ["topic", strings.topicLabel, strings.topicPlaceholder],
  ] as const;

  return (
    <div className="space-y-6">
      <h2 className="hidden font-semibold text-2xl text-foreground print:block">
        {strings.worksheetTitle}
      </h2>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.sourceHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {sourceFields.map(([key, label, placeholder]) => (
            <TextField
              id={`craap-${key}`}
              key={key}
              label={label}
              onChange={(value) =>
                setSource((current) => ({ ...current, [key]: value }))
              }
              placeholder={placeholder}
              value={source[key]}
            />
          ))}
          <TextField
            id="craap-date"
            label={strings.dateLabel}
            onChange={(value) =>
              setSource((current) => ({ ...current, date: value }))
            }
            type="date"
            value={source.date}
          />
        </div>
      </section>

      <div className="print:hidden">
        <Notice>{strings.lateral}</Notice>
      </div>

      {CRITERIA.map((criterion) => (
        <section
          className={`${CARD} break-inside-avoid space-y-2`}
          key={criterion}
        >
          <h3 className={WIDGET_H3}>
            {strings.criteria[criterion]}
            <span className="ml-2 font-normal text-muted-foreground text-sm">
              {strings.definitions[criterion]}
            </span>
          </h3>
          {strings.questions[criterion].map((question, index) => (
            <QuestionRow
              id={`craap-${criterion}-${index}`}
              key={question}
              onChange={(rating) => setAnswer(criterion, index, rating)}
              question={question}
              strings={strings}
              value={answers[criterion][index] ?? null}
            />
          ))}
          <TextAreaField
            id={`craap-notes-${criterion}`}
            label={t(strings.notesLabel, {
              criterion: strings.criteria[criterion],
            })}
            onChange={(value) =>
              setNotes((current) => ({ ...current, [criterion]: value }))
            }
            placeholder={strings.notesPlaceholder}
            prose
            rows={2}
            value={notes[criterion]}
          />
        </section>
      ))}

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <p className={HINT}>
          {tPlural(strings.progress, locale, answered, {
            count: fmt(answered),
            total: fmt(totalQuestions),
          })}
        </p>
        <StatGrid>
          {result.criteria.map((c) => (
            <StatCard
              key={c.criterion}
              label={t(strings.criterionScore, {
                criterion: strings.criteria[c.criterion],
              })}
              value={score(c.score)}
            />
          ))}
          <StatCard
            emphasis
            label={strings.totalLabel}
            value={score(result.total)}
          />
        </StatGrid>
        {result.band ? (
          <p className="text-foreground">
            <span className="font-semibold">
              {strings.bandNames[result.band]}.
            </span>{" "}
            {strings.bandAdvice[result.band]}
          </p>
        ) : (
          <Notice>{strings.incomplete}</Notice>
        )}
        {result.weak.length > 0 ? (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-foreground text-sm">
            {t(strings.weakSpot, {
              criteria: listFormat(
                result.weak.map((c) => strings.criteria[c]),
                locale
              ),
            })}
          </p>
        ) : null}
        <p className={HINT}>{strings.bandKey}</p>
        <p className={HINT}>{strings.credit}</p>
        <div className="flex flex-wrap gap-3 print:hidden">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={summary}
          />
          <button className={BUTTON_SECONDARY} onClick={reset} type="button">
            {strings.reset}
          </button>
        </div>
      </section>
    </div>
  );
}
